import { useState, useRef, useCallback } from 'react'
import { Upload, Film, Music, FileVideo, Info, X, ChevronDown, ChevronRight, Wrench, RefreshCw, Download } from 'lucide-react'

// ─── MediaInfo Loader (CDN) ───────────────────────────────────────────────────
// Il bundle UMD di mediainfo.js@0.3.7 esporta window.MediaInfo come OGGETTO
// con proprietà .default e .mediaInfoFactory — non direttamente come funzione.
let _mediaInfoPromise = null

const loadMediaInfo = () => {
  if (_mediaInfoPromise) return _mediaInfoPromise
  _mediaInfoPromise = new Promise((resolve, reject) => {
    // Se già caricato, estrai subito il factory
    if (window.MediaInfo) {
      const f = window.MediaInfo?.default || window.MediaInfo?.mediaInfoFactory
      if (typeof f === 'function') { resolve(f); return }
    }
    const script = document.createElement('script')
    script.src = 'https://cdn.jsdelivr.net/npm/mediainfo.js@0.3.7/dist/umd/index.js'
    script.async = true
    script.onload = () => {
      // Il global è un oggetto esports — il factory è su .default o .mediaInfoFactory
      const factory = window.MediaInfo?.default || window.MediaInfo?.mediaInfoFactory
      if (typeof factory === 'function') {
        resolve(factory)
      } else {
        _mediaInfoPromise = null
        reject(new Error('MediaInfo non disponibile dopo il caricamento'))
      }
    }
    script.onerror = () => {
      _mediaInfoPromise = null
      reject(new Error('Impossibile caricare MediaInfo dal CDN'))
    }
    document.head.appendChild(script)
  })
  return _mediaInfoPromise
}

// ─── FFmpeg Loader (CDN) ──────────────────────────────────────────────────────
// Usiamo @ffmpeg/ffmpeg@0.11.6: il Worker viene creato come blob URL + importScripts
// → nessun problema cross-origin, nessun SharedArrayBuffer richiesto.
let _ffmpegCache = null
let _ffmpegLoadPromise = null

const _loadScript = (src) => new Promise((resolve, reject) => {
  if (document.querySelector(`script[src="${src}"]`)) { resolve(); return }
  const s = document.createElement('script')
  s.src = src; s.async = true
  s.onload = resolve
  s.onerror = () => reject(new Error(`Impossibile caricare: ${src}`))
  document.head.appendChild(s)
})

const getFFmpeg = () => {
  if (_ffmpegCache) return Promise.resolve(_ffmpegCache)
  if (_ffmpegLoadPromise) return _ffmpegLoadPromise
  _ffmpegLoadPromise = (async () => {
    try {
      await _loadScript('https://cdn.jsdelivr.net/npm/@ffmpeg/ffmpeg@0.11.6/dist/ffmpeg.min.js')
      const { createFFmpeg, fetchFile } = window.FFmpeg || {}
      if (!createFFmpeg) throw new Error('FFmpeg non trovato nel bundle CDN')
      const ff = createFFmpeg({
        corePath: 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.11.0/dist/ffmpeg-core.js',
        log: false,
      })
      await ff.load()
      _ffmpegCache = { ff, fetchFile }
      _ffmpegLoadPromise = null
      return _ffmpegCache
    } catch (e) {
      _ffmpegCache = null
      _ffmpegLoadPromise = null
      throw e
    }
  })()
  return _ffmpegLoadPromise
}

// ─── MediaInfo Helpers ────────────────────────────────────────────────────────

const readChunk = (file) => (chunkSize, offset) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      if (e.target.error) reject(e.target.error)
      else resolve(new Uint8Array(e.target.result))
    }
    reader.readAsArrayBuffer(file.slice(offset, offset + chunkSize))
  })

const TRACK_CFG = {
  General: { label: 'Generale',      icon: Info,      color: 'text-violet-400',  bg: 'bg-violet-500/10',  border: 'border-violet-500/20'  },
  Video:   { label: 'Video',          icon: Film,      color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  Audio:   { label: 'Audio',          icon: Music,     color: 'text-blue-400',    bg: 'bg-blue-500/10',    border: 'border-blue-500/20'    },
  Text:    { label: 'Sottotitoli',    icon: FileVideo, color: 'text-yellow-400',  bg: 'bg-yellow-500/10',  border: 'border-yellow-500/20'  },
  Other:   { label: 'Altro',          icon: Info,      color: 'text-gray-400',    bg: 'bg-gray-500/10',    border: 'border-gray-500/20'    },
}

const KEY_FIELDS = {
  General: ['Format','Format_Version','FileSize','Duration','OverallBitRate','Encoded_Date','File_Modified_Date'],
  Video:   ['Format','Format_Profile','CodecID','Width','Height','FrameRate','BitRate','BitDepth','ColorSpace','ChromaSubsampling','ScanType','HDR_Format','colour_primaries','transfer_characteristics'],
  Audio:   ['Format','CodecID','Channels','ChannelPositions','SamplingRate','BitRate','BitDepth','Language'],
  Text:    ['Format','CodecID','Language'],
}

const FIELD_LABELS = {
  Format:'Formato', Format_Version:'Versione', Format_Profile:'Profilo', CodecID:'Codec ID',
  FileSize:'Dimensione', Duration:'Durata', OverallBitRate:'Bitrate totale',
  Encoded_Date:'Data codifica', File_Modified_Date:'Data modifica',
  Width:'Larghezza', Height:'Altezza', FrameRate:'Frame rate', BitRate:'Bitrate',
  BitDepth:'Profondità bit', ColorSpace:'Spazio colore', ChromaSubsampling:'Chroma',
  ScanType:'Tipo scansione', HDR_Format:'HDR', colour_primaries:'Primarie colore',
  transfer_characteristics:'Transfer', Channels:'Canali', ChannelPositions:'Posizione canali',
  SamplingRate:'Sample rate', Language:'Lingua',
}

const fmtVal = (key, value) => {
  if (value == null || value === '') return null
  const v = String(value)
  if (key === 'FileSize') {
    const n = parseInt(v)
    if (n > 1e9) return `${(n/1e9).toFixed(2)} GB`
    if (n > 1e6) return `${(n/1e6).toFixed(1)} MB`
    if (n > 1e3) return `${(n/1e3).toFixed(0)} KB`
    return `${n} B`
  }
  if (key === 'Duration') {
    const s = Math.round(parseFloat(v) / 1000)
    const h = Math.floor(s/3600), m = Math.floor((s%3600)/60), sec = s%60
    if (h > 0) return `${h}h ${m}m ${sec}s`
    if (m > 0) return `${m}m ${sec}s`
    return `${sec}s`
  }
  if (key.includes('BitRate') || key === 'OverallBitRate') {
    const n = parseInt(v)
    if (n > 1e6) return `${(n/1e6).toFixed(1)} Mbps`
    if (n > 1e3) return `${(n/1e3).toFixed(0)} kbps`
    return `${n} bps`
  }
  if (key === 'SamplingRate') return `${(parseInt(v)/1000).toFixed(1)} kHz`
  if (key === 'Width' || key === 'Height') return `${v} px`
  if (key === 'FrameRate') return `${parseFloat(v).toFixed(3)} fps`
  if (key === 'BitDepth') return `${v} bit`
  if (key === 'Channels') return `${v} ch`
  return v
}

// ─── Converter Config ─────────────────────────────────────────────────────────

const VIDEO_IN = ['mp4','mov','avi','mkv','webm','flv','wmv','m4v','mts','m2ts','ts','3gp','ogv','vob']
const AUDIO_IN = ['mp3','wav','aac','flac','ogg','m4a','wma','opus','aiff']

const VIDEO_OUT = [
  { ext: 'mp4',  label: 'MP4',  desc: 'H.264 + AAC — universale'  },
  { ext: 'mov',  label: 'MOV',  desc: 'Apple QuickTime'            },
  { ext: 'avi',  label: 'AVI',  desc: 'Audio Video Interleave'     },
  { ext: 'mkv',  label: 'MKV',  desc: 'Matroska — multi-traccia'   },
  { ext: 'webm', label: 'WebM', desc: 'Web — VP8 + Vorbis'         },
  { ext: '3gp',  label: '3GP',  desc: 'Mobile — 3GPP'              },
  { ext: 'ogv',  label: 'OGV',  desc: 'Ogg Video — open source'   },
  { ext: 'gif',  label: 'GIF',  desc: 'Animato — 480px, 10fps'    },
]

const AUDIO_OUT = [
  { ext: 'mp3',  label: 'MP3',  desc: 'MPEG Audio — universale'   },
  { ext: 'aac',  label: 'AAC',  desc: 'Advanced Audio Coding'     },
  { ext: 'wav',  label: 'WAV',  desc: 'PCM — non compresso'       },
  { ext: 'flac', label: 'FLAC', desc: 'Lossless — qualità massima' },
  { ext: 'ogg',  label: 'OGG',  desc: 'Ogg Vorbis — open source'  },
  { ext: 'm4a',  label: 'M4A',  desc: 'MPEG-4 Audio (Apple)'      },
  { ext: 'opus', label: 'Opus', desc: 'Alta efficienza — VoIP'    },
]

const MIME = {
  mp4:'video/mp4', mov:'video/quicktime', avi:'video/x-msvideo',
  mkv:'video/x-matroska', webm:'video/webm', '3gp':'video/3gpp',
  ogv:'video/ogg', gif:'image/gif',
  mp3:'audio/mpeg', aac:'audio/aac', wav:'audio/wav',
  flac:'audio/flac', ogg:'audio/ogg', m4a:'audio/mp4', opus:'audio/opus',
}

const buildFFmpegArgs = (inputName, outputName, inputExt, outputExt) => {
  const isVideoIn  = VIDEO_IN.includes(inputExt)
  const isAudioOut = AUDIO_OUT.some(f => f.ext === outputExt)
  const args = ['-i', inputName]
  if (isVideoIn && isAudioOut) args.push('-vn')
  switch (outputExt) {
    case 'mp4':  args.push('-c:v','libx264','-preset','fast','-c:a','aac','-movflags','+faststart'); break
    case 'mov':  args.push('-c:v','libx264','-preset','fast','-c:a','aac'); break
    case 'avi':  args.push('-c:v','libx264','-preset','fast','-c:a','libmp3lame'); break
    case 'mkv':  args.push('-c:v','libx264','-preset','fast','-c:a','aac'); break
    case 'webm': args.push('-c:v','libvpx','-b:v','1M','-c:a','libvorbis'); break
    case '3gp':  args.push('-c:v','libx264','-preset','fast','-c:a','aac','-vf','scale=320:-2'); break
    case 'ogv':  args.push('-c:v','libtheora','-c:a','libvorbis'); break
    case 'gif':  args.push('-vf','fps=10,scale=480:-1:flags=lanczos','-loop','0'); break
    case 'mp3':  args.push('-c:a','libmp3lame','-q:a','2'); break
    case 'wav':  args.push('-c:a','pcm_s16le'); break
    case 'aac':  args.push('-c:a','aac','-b:a','192k'); break
    case 'flac': args.push('-c:a','flac'); break
    case 'ogg':  args.push('-c:a','libvorbis','-q:a','4'); break
    case 'm4a':  args.push('-c:a','aac','-b:a','192k'); break
    case 'opus': args.push('-c:a','libopus','-b:a','128k'); break
    default: break
  }
  args.push(outputName)
  return args
}

// ─── TrackCard ────────────────────────────────────────────────────────────────
const TrackCard = ({ track, index }) => {
  const [open, setOpen] = useState(true)
  const type = track['@type'] || 'Other'
  const cfg  = TRACK_CFG[type] || TRACK_CFG.Other
  const Icon = cfg.icon

  const primaryKeys = KEY_FIELDS[type] || []
  const shown = new Set(primaryKeys)

  const rows = [
    ...primaryKeys.map(k => ({ k, label: FIELD_LABELS[k] || k, val: fmtVal(k, track[k]) })).filter(r => r.val),
    ...Object.entries(track)
      .filter(([k, v]) => !shown.has(k) && !k.startsWith('@') && v != null && String(v).trim())
      .map(([k, v]) => ({ k, label: FIELD_LABELS[k] || k, val: fmtVal(k, v) }))
      .filter(r => r.val),
  ]

  const title = type === 'General' ? 'Informazioni generali'
    : `${cfg.label}${index > 0 ? ` #${index}` : ''}`

  return (
    <div className={`border ${cfg.border} rounded-xl overflow-hidden`}>
      <button onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-4 py-3 ${cfg.bg} hover:brightness-110 transition-all`}>
        <div className="flex items-center gap-2">
          <Icon size={15} className={cfg.color} />
          <span className={`text-sm font-semibold ${cfg.color}`}>{title}</span>
          {track.Format && <span className="text-xs text-gray-500 font-mono">{track.Format}</span>}
          {type === 'Video' && track.Width && track.Height &&
            <span className="text-xs text-gray-500">{track.Width}×{track.Height}</span>}
        </div>
        {open
          ? <ChevronDown size={13} className="text-gray-500 flex-shrink-0" />
          : <ChevronRight size={13} className="text-gray-500 flex-shrink-0" />}
      </button>
      {open && (
        <div className="divide-y divide-gray-800/50">
          {rows.map(({ k, label, val }) => (
            <div key={k} className="flex items-baseline gap-3 px-4 py-2 hover:bg-gray-800/20 transition-colors">
              <span className="text-xs text-gray-500 w-44 flex-shrink-0 leading-relaxed">{label}</span>
              <span className="text-sm text-white font-mono break-all leading-relaxed">{val}</span>
            </div>
          ))}
          {rows.length === 0 &&
            <p className="px-4 py-3 text-xs text-gray-600">Nessun dato disponibile</p>}
        </div>
      )}
    </div>
  )
}

// ─── MediaInfo Tool ───────────────────────────────────────────────────────────
const MediaInfoTool = () => {
  const [file, setFile]         = useState(null)
  const [result, setResult]     = useState(null)
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState(null)
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef()

  const analyze = useCallback(async (f) => {
    if (!f) return
    setFile(f)
    setResult(null)
    setError(null)
    setLoading(true)
    try {
      const MediaInfoFactory = await loadMediaInfo()
      const mi = await MediaInfoFactory({
        format: 'object',
        locateFile: (path) => `https://cdn.jsdelivr.net/npm/mediainfo.js@0.3.7/dist/${path}`,
      })
      const info = await mi.analyzeData(() => f.size, readChunk(f))
      mi.close()
      setResult(info)
    } catch (e) {
      console.error('[MediaInfo]', e)
      setError(e.message || 'Errore durante l\'analisi. Prova con un altro file.')
    } finally {
      setLoading(false)
    }
  }, [])

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false)
    const f = e.dataTransfer?.files?.[0]
    if (f) analyze(f)
  }, [analyze])

  const reset = () => { setFile(null); setResult(null); setError(null) }

  const tracks = result?.media?.track || []
  const byType = {}
  tracks.forEach(t => {
    const tp = t['@type'] || 'Other'
    if (!byType[tp]) byType[tp] = []
    byType[tp].push(t)
  })

  return (
    <div className="space-y-4">
      {!file && (
        <div
          onDrop={onDrop}
          onDragOver={e => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
            dragging ? 'border-violet-500 bg-violet-500/5' : 'border-gray-700 hover:border-gray-600 hover:bg-gray-800/30'
          }`}
        >
          <input ref={inputRef} type="file" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) analyze(f) }}
            accept="video/*,audio/*,.mkv,.avi,.mov,.mp4,.m4v,.mts,.m2ts,.ts,.flv,.webm,.wmv,.mp3,.aac,.flac,.wav,.m4a,.ogg" />
          <Upload size={36} className="mx-auto mb-3 text-gray-500" />
          <p className="text-sm font-medium text-gray-300">Trascina un file video o audio</p>
          <p className="text-xs text-gray-600 mt-1">oppure clicca per scegliere — MP4, MOV, MKV, AVI, MXF, MP3, FLAC e altri</p>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center gap-3 py-10">
          <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-400">Analisi in corso...</span>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={reset} className="text-gray-500 hover:text-white flex-shrink-0"><X size={16} /></button>
        </div>
      )}

      {result && file && (
        <div className="space-y-3">
          <div className="flex items-center justify-between bg-gray-800/50 border border-gray-800 rounded-xl px-4 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <FileVideo size={18} className="text-violet-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate">{file.name}</p>
                <p className="text-xs text-gray-500">
                  {(file.size / 1e6).toFixed(1)} MB · {tracks.length} track{tracks.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <button onClick={reset} title="Analizza un altro file"
              className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-gray-700 transition-colors flex-shrink-0">
              <X size={16} />
            </button>
          </div>
          {['General','Video','Audio','Text','Other'].map(type =>
            (byType[type] || []).map((track, i) => (
              <TrackCard key={`${type}-${i}`} track={track} index={i} />
            ))
          )}
        </div>
      )}
    </div>
  )
}

// ─── Converter Tool ───────────────────────────────────────────────────────────
const ConverterTool = () => {
  const [file, setFile]           = useState(null)
  const [outputExt, setOutputExt] = useState(null)
  const [status, setStatus]       = useState('idle') // idle | loading | converting | done | error
  const [progress, setProgress]   = useState(0)
  const [error, setError]         = useState(null)
  const [outputUrl, setOutputUrl] = useState(null)
  const [outputName, setOutputName] = useState(null)
  const [dragging, setDragging]   = useState(false)
  const inputRef  = useRef()
  const outputRef = useRef(null)

  const inputExt    = file ? file.name.split('.').pop().toLowerCase() : null
  const isVideoIn   = inputExt ? VIDEO_IN.includes(inputExt) : false
  const allInputExt = [...VIDEO_IN, ...AUDIO_IN].map(e => `.${e}`).join(',')

  const handleFile = (f) => {
    const ext = f.name.split('.').pop().toLowerCase()
    if (![...VIDEO_IN, ...AUDIO_IN].includes(ext)) {
      setError(`Formato .${ext} non supportato`)
      return
    }
    if (outputRef.current) { URL.revokeObjectURL(outputRef.current); outputRef.current = null }
    setFile(f); setOutputExt(null); setOutputUrl(null)
    setOutputName(null); setError(null); setStatus('idle'); setProgress(0)
  }

  const convert = async () => {
    if (!file || !outputExt) return
    setStatus('loading'); setError(null); setProgress(0)
    try {
      const { ff, fetchFile } = await getFFmpeg()
      setStatus('converting')

      ff.setProgress(({ ratio }) => {
        if (ratio >= 0) setProgress(Math.round(Math.min(ratio, 1) * 100))
      })

      const inName  = `input.${inputExt}`
      const outName = `output.${outputExt}`
      ff.FS('writeFile', inName, await fetchFile(file))
      await ff.run(...buildFFmpegArgs(inName, outName, inputExt, outputExt))

      const data = ff.FS('readFile', outName)
      const blob = new Blob([data.buffer], { type: MIME[outputExt] || 'application/octet-stream' })
      const url  = URL.createObjectURL(blob)
      outputRef.current = url

      try { ff.FS('unlink', inName) } catch (_) {}
      try { ff.FS('unlink', outName) } catch (_) {}

      const base = file.name.replace(/\.[^.]+$/, '')
      setOutputName(`${base}.${outputExt}`)
      setOutputUrl(url)
      setStatus('done')
      setProgress(100)
    } catch (e) {
      console.error('[FFmpeg]', e)
      setError(e.message || 'Errore durante la conversione.')
      setStatus('error')
    }
  }

  const reset = () => {
    if (outputRef.current) { URL.revokeObjectURL(outputRef.current); outputRef.current = null }
    setFile(null); setOutputExt(null); setOutputUrl(null)
    setOutputName(null); setError(null); setStatus('idle'); setProgress(0)
  }

  // Format button helper
  const FmtBtn = ({ ext, label, desc, activeColor, activeBorder }) => (
    <button
      key={ext}
      onClick={() => setOutputExt(ext)}
      className={`px-3 py-2.5 rounded-xl text-left border transition-all ${
        outputExt === ext
          ? `${activeColor} ${activeBorder} text-white`
          : 'bg-gray-800/50 border-gray-700 text-gray-300 hover:border-gray-600 hover:bg-gray-800'
      }`}
    >
      <p className="text-sm font-bold">{label}</p>
      <p className="text-[10px] text-gray-500 leading-tight mt-0.5 line-clamp-2">{desc}</p>
    </button>
  )

  return (
    <div className="space-y-5">

      {/* Drop zone */}
      {!file && (
        <div
          onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer?.files?.[0]; if (f) handleFile(f) }}
          onDragOver={e => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
            dragging ? 'border-orange-500 bg-orange-500/5' : 'border-gray-700 hover:border-gray-600 hover:bg-gray-800/30'
          }`}
        >
          <input ref={inputRef} type="file" className="hidden" accept={allInputExt}
            onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f) }} />
          <Upload size={36} className="mx-auto mb-3 text-gray-500" />
          <p className="text-sm font-medium text-gray-300">Trascina un file video o audio</p>
          <p className="text-xs text-gray-600 mt-1">MP4, MOV, MKV, AVI, WebM, FLV, MP3, WAV, FLAC, AAC, OGG e altri</p>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={() => setError(null)} className="text-gray-500 hover:text-white flex-shrink-0"><X size={16} /></button>
        </div>
      )}

      {/* File loaded — format picker */}
      {file && (status === 'idle') && (
        <div className="space-y-4">
          {/* File info bar */}
          <div className="flex items-center gap-3 bg-gray-800/50 border border-gray-800 rounded-xl px-4 py-3">
            <FileVideo size={18} className="text-orange-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{file.name}</p>
              <p className="text-xs text-gray-500">{(file.size / 1e6).toFixed(1)} MB · .{inputExt?.toUpperCase()}</p>
            </div>
            <button onClick={reset} className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-gray-700 transition-colors flex-shrink-0">
              <X size={16} />
            </button>
          </div>

          {/* Video output formats */}
          {isVideoIn && (
            <>
              <div>
                <p className="text-[11px] text-gray-600 uppercase tracking-widest font-semibold mb-2">Video</p>
                <div className="grid grid-cols-4 sm:grid-cols-4 gap-2">
                  {VIDEO_OUT.map(({ ext, label, desc }) => (
                    <FmtBtn key={ext} ext={ext} label={label} desc={desc}
                      activeColor="bg-orange-500/20" activeBorder="border-orange-500/40" />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[11px] text-gray-600 uppercase tracking-widest font-semibold mb-2">Estrai audio</p>
                <div className="grid grid-cols-4 sm:grid-cols-4 gap-2">
                  {AUDIO_OUT.map(({ ext, label, desc }) => (
                    <FmtBtn key={ext} ext={ext} label={label} desc={desc}
                      activeColor="bg-blue-500/20" activeBorder="border-blue-500/40" />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Audio only */}
          {!isVideoIn && (
            <div>
              <p className="text-[11px] text-gray-600 uppercase tracking-widest font-semibold mb-2">Audio</p>
              <div className="grid grid-cols-4 gap-2">
                {AUDIO_OUT.map(({ ext, label, desc }) => (
                  <FmtBtn key={ext} ext={ext} label={label} desc={desc}
                    activeColor="bg-blue-500/20" activeBorder="border-blue-500/40" />
                ))}
              </div>
            </div>
          )}

          {/* Convert button */}
          {outputExt && (
            <button onClick={convert}
              className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-semibold py-3 rounded-xl transition-colors">
              <RefreshCw size={16} />
              Converti in {outputExt.toUpperCase()}
            </button>
          )}
        </div>
      )}

      {/* Loading FFmpeg */}
      {status === 'loading' && (
        <div className="flex flex-col items-center justify-center gap-3 py-12">
          <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-300 font-medium">Caricamento FFmpeg...</p>
          <p className="text-xs text-gray-600">~9 MB · solo la prima volta, poi è in cache</p>
        </div>
      )}

      {/* Converting */}
      {status === 'converting' && (
        <div className="space-y-4 py-4">
          <div className="flex items-center gap-3 bg-gray-800/50 border border-gray-800 rounded-xl px-4 py-3">
            <FileVideo size={18} className="text-orange-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{file.name}</p>
              <p className="text-xs text-gray-500">→ {outputExt?.toUpperCase()}</p>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-gray-500">Conversione in corso...</span>
              <span className="text-orange-400 font-mono font-semibold">{progress}%</span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
              <div className="bg-orange-500 h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${Math.max(progress, 3)}%` }} />
            </div>
          </div>
          <p className="text-xs text-gray-700 text-center">La velocità dipende dalla dimensione e dal formato del file</p>
        </div>
      )}

      {/* Done */}
      {status === 'done' && outputUrl && (
        <div className="space-y-3">
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl px-4 py-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-green-400">Conversione completata!</p>
              <p className="text-xs text-gray-500 mt-0.5 truncate">{outputName}</p>
            </div>
            <a href={outputUrl} download={outputName}
              className="flex items-center gap-2 bg-green-500 hover:bg-green-400 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors flex-shrink-0">
              <Download size={15} />
              Scarica
            </a>
          </div>
          <button onClick={reset}
            className="w-full text-sm text-gray-600 hover:text-gray-400 py-2 transition-colors">
            Converti un altro file
          </button>
        </div>
      )}

    </div>
  )
}

// ─── Tools page ───────────────────────────────────────────────────────────────
const TOOLS = [
  {
    id: 'mediainfo',
    label: 'MediaInfo',
    description: 'Analizza video e audio: codec, risoluzione, bitrate, HDR, canali audio e tutti i metadati tecnici. Gira nel browser, nessun upload.',
    icon: Film,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
    component: MediaInfoTool,
  },
  {
    id: 'converter',
    label: 'Convertitore',
    description: 'Converti video e audio tra tutti i formati principali: MP4, MOV, MKV, WebM, GIF, MP3, WAV, FLAC, AAC, Opus e altri. Tutto nel browser, nessun upload.',
    icon: RefreshCw,
    color: 'text-orange-400',
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/20',
    component: ConverterTool,
  },
]

const Tools = () => {
  const [activeTool, setActiveTool] = useState(TOOLS[0].id)
  const tool = TOOLS.find(t => t.id === activeTool)
  const ToolComp = tool?.component

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Wrench size={22} className="text-violet-400" />
        <h1 className="text-2xl font-bold text-white">Tools</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Lista strumenti */}
        <div className="lg:col-span-1">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-3 space-y-1">
            {TOOLS.map(t => {
              const Icon = t.icon
              const active = t.id === activeTool
              return (
                <button key={t.id} onClick={() => setActiveTool(t.id)}
                  className={`w-full flex items-start gap-3 px-3 py-3 rounded-xl text-left transition-colors ${
                    active ? 'bg-violet-600/20 border border-violet-500/30' : 'hover:bg-gray-800 border border-transparent'
                  }`}>
                  <div className={`w-8 h-8 rounded-lg ${t.bg} border ${t.border} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <Icon size={15} className={t.color} />
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-semibold ${active ? 'text-white' : 'text-gray-300'}`}>{t.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5 leading-snug line-clamp-2">{t.description}</p>
                  </div>
                </button>
              )
            })}
            <p className="text-[10px] text-gray-700 text-center py-2">Altri strumenti in arrivo</p>
          </div>
        </div>

        {/* Contenuto */}
        <div className="lg:col-span-3">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
            {tool && (
              <>
                <div className="mb-5 pb-4 border-b border-gray-800">
                  <h2 className="text-base font-bold text-white">{tool.label}</h2>
                  <p className="text-sm text-gray-500 mt-0.5">{tool.description}</p>
                </div>
                {ToolComp && <ToolComp />}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Tools
